import { ReactElement, useCallback, useEffect, useState } from 'react';

import { faPen, faTrash } from '@awesome.me/kit-379289146f/icons/classic/light';
import {
  faCoreLogo,
  faEngageLogo,
  faProgressLogo,
  faProtectLogo,
  faTaxplanLogo,
} from '@awesome.me/kit-379289146f/icons/kit/custom';
import { Table, TableColumn } from '@changegps/horizon-component-library';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Avatar, Box, IconButton, Stack, Typography } from '@mui/material';
import { useBoolean } from 'usehooks-ts';

import { useUsers } from '../hooks';
import { usersStore } from '../store';
import { User, UserDetails } from '../types';

import { DeleteUserDialog } from './DeleteUserDialog';
import { ProjectChip } from './ProjectChip';
import { UserDrawer } from './UserDrawer';

export const UserTable = (): ReactElement => {
  const { data, isFetching, isError, refetch } = useUsers();

  const { page, rowsPerPage, setPage, setRowsPerPage } = usersStore();

  const [openDrawer, setOpenDrawer] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserDetails | null>(null);
  const [userToDelete, setUserToDelete] = useState<string | null>(null);
  const { value: shouldShowDeleteDialog, toggle: toggleDeleteDialog } =
    useBoolean();

  const columns: TableColumn<User>[] = [
    {
      title: 'ID',
      key: 'id',
      render: (value: string | number | string[]) => (
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography maxWidth="50ch" fontSize="inherit">
            {String(value) || 'Untitled'}
          </Typography>
        </Stack>
      ),
    },
    {
      title: 'User',
      key: 'name',
      render: (value: string | number | string[], record: User) => (
        <Box
          sx={{
            display: 'flex',
            gap: '12px',
            alignItems: 'center',
          }}
        >
          <Avatar alt={String(value)} src={record.profile_pic} />
          <Box>
            <Typography
              sx={{
                fontSize: '15px',
                lineHeight: '24px',
              }}
            >
              {String(value)}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                gap: '4px',
              }}
            >
              {record.roles.map((role, index) => (
                <ProjectChip key={`${role}-${index}`} projectName={role} />
              ))}
            </Box>
          </Box>
        </Box>
      ),
    },
    {
      title: 'Email',
      key: 'email',
      render: (value: string | number | string[]) => (
        <Typography
          sx={{
            fontSize: '13px',
            lineHeight: '24px',
          }}
        >
          {String(value)}
        </Typography>
      ),
    },
    {
      title: 'Products',
      key: 'products',
      render: (value: string | number | string[], _: User) => (
        <Box
          sx={{
            display: 'flex',
            gap: '5px',
          }}
        >
          {(Array.isArray(value) ? value : []).map((projectName, index) => {
            const icon = getProjectIcon(projectName);
            const displayName =
              projectName.toLowerCase() === 'taxplan'
                ? 'Tax Plan'
                : projectName;

            return (
              <ProjectChip
                key={`${projectName}-${index}`}
                projectName={displayName}
                icon={
                  icon ? (
                    <FontAwesomeIcon
                      size="lg"
                      color="#828D98"
                      icon={icon as IconProp}
                    />
                  ) : null
                }
              />
            );
          })}
        </Box>
      ),
    },
    {
      title: 'Actions',
      key: 'uuid',
      render: (_: string | number | string[], record: User) => (
        <Stack direction="row" spacing={0.5}>
          <IconButton
            aria-label="Edit Button"
            size="small"
            color="inherit"
            onClick={() => handleEdit(record as unknown as UserDetails)}
          >
            <FontAwesomeIcon icon={faPen as IconProp} />
          </IconButton>

          <IconButton
            aria-label="Delete Button"
            size="small"
            color="inherit"
            onClick={() => handleDelete(record.uuid)}
          >
            <FontAwesomeIcon icon={faTrash as IconProp} />
          </IconButton>
        </Stack>
      ),
    },
  ];

  const getProjectIcon = (projectName: string) => {
    switch (projectName.toLowerCase()) {
      case 'core':
        return faCoreLogo;
      case 'engage':
        return faEngageLogo;
      case 'progress':
        return faProgressLogo;
      case 'protect':
        return faProtectLogo;
      case 'taxplan':
        return faTaxplanLogo;
      default:
        return null;
    }
  };

  const handleEdit = (user: UserDetails) => {
    setSelectedUser(user);
    setOpenDrawer(true);
  };

  const handleDelete = (uuid: string) => {
    setUserToDelete(uuid);
    toggleDeleteDialog();
  };

  const handleCloseDrawer = () => {
    setOpenDrawer(false);
    setSelectedUser(null);
  };

  const handleCloseDeleteDialog = () => {
    toggleDeleteDialog();
    setUserToDelete(null);
  };

  const handlePageChange = useCallback(
    (newPage: number) => {
      setPage(newPage);
    },
    [setPage],
  );

  const handleChangeRowsPerPage = useCallback(
    (pageSize: number) => {
      setRowsPerPage(pageSize);
      setPage(1);
    },
    [setRowsPerPage, setPage],
  );

  useEffect(() => {
    if (data && data.last_page > 0 && page > data.last_page) {
      setPage(data.last_page);
    }
  }, [data, page, setPage]);

  return (
    <>
      <Table<User>
        columns={columns}
        data={data?.data}
        isLoading={isFetching}
        isError={isError}
        refetch={refetch}
        rowKey="uuid" // or use (record) => record.uuid if you prefer function
        paginationConfig={{
          page: page,
          pageSize: rowsPerPage,
          lastPage: data?.last_page || 1,
          onChangePage: handlePageChange,
          onChangePageSize: handleChangeRowsPerPage,
        }}
      />

      {selectedUser && (
        <UserDrawer
          open={openDrawer}
          onClose={handleCloseDrawer}
          user={selectedUser}
        />
      )}

      {userToDelete && (
        <DeleteUserDialog
          uuid={userToDelete}
          open={shouldShowDeleteDialog}
          onClose={handleCloseDeleteDialog}
        />
      )}
    </>
  );
};
